/**
 * Drawn icon set — the React port of the design handoff's `operator-icons.js`.
 *
 * The handoff painted these by scanning for `<span class="i" data-i="close">`
 * and injecting SVG from a MutationObserver. That approach fights React's
 * reconciliation (the observer re-paints nodes React is about to replace), so
 * the same marks live here as components instead. The rendered markup is
 * byte-identical to what `paint()` produced, which is what lets the handoff's
 * `.i` / `.i svg` rules in operator-premium.css size them unchanged.
 *
 * Two marks at the end are additions, not ports; each says why it exists.
 *
 * Every path is authored on a 16×16 grid with a 1.6 stroke. Do not mix in
 * icons drawn on a different grid — `vector-effect:non-scaling-stroke` means a
 * 24×24 path renders visibly thinner beside these.
 */

const PATHS = {
  close: <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" />,
  minimize: <path d="M4 8h8" />,
  expand: <path d="M6.2 9.8 3 13m0 0h3.4M3 13V9.6M9.8 6.2 13 3m0 0H9.6M13 3v3.4" />,
  restart: <><path d="M13 8a5 5 0 1 1-1.6-3.7" /><path d="M13 2.6v2.6h-2.6" /></>,
  sound: (
    <>
      <path d="M7.4 3.6 4.8 5.9H3v4.2h1.8l2.6 2.3z" />
      <path d="M10.2 6.1a2.7 2.7 0 0 1 0 3.8" />
      <path d="M12 4.3a5.2 5.2 0 0 1 0 7.4" />
    </>
  ),
  menu: (
    <>
      <circle cx="8" cy="3.6" r=".9" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r=".9" fill="currentColor" stroke="none" />
      <circle cx="8" cy="12.4" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  email: (
    <>
      <rect x="2.4" y="4" width="11.2" height="8" rx="1.8" />
      <path d="m3.2 5 4.8 3.6L12.8 5" />
    </>
  ),
  paperclip: (
    <path d="M11.4 7.3 7.8 10.9a2.3 2.3 0 0 1-3.2-3.2l4.3-4.3a1.6 1.6 0 0 1 2.3 2.3L7 9.8a.9.9 0 0 1-1.3-1.3l3.3-3.3" />
  ),
  smile: (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M5.8 9.4a2.7 2.7 0 0 0 4.4 0" />
      <circle cx="6.2" cy="6.4" r=".8" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="6.4" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  mic: (
    <>
      <rect x="6.3" y="2.4" width="3.4" height="6.6" rx="1.7" />
      <path d="M4.2 8.2a3.8 3.8 0 0 0 7.6 0" />
      <path d="M8 12v1.6" />
    </>
  ),
  "arrow-up": <><path d="M8 12.6V3.8" /><path d="M4.4 7.2 8 3.6l3.6 3.6" /></>,
  "arrow-up-right": <><path d="M4.4 11.6 11.6 4.4" /><path d="M6.6 4.4h5v5" /></>,
  "chevron-down": <path d="m4.4 6.4 3.6 3.6 3.6-3.6" />,
  "face-frown": (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M5.9 10.6a2.7 2.7 0 0 1 4.2 0" />
      <circle cx="6.2" cy="6.6" r=".8" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="6.6" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  "face-meh": (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M5.9 10.1h4.2" />
      <circle cx="6.2" cy="6.6" r=".8" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="6.6" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  "face-smile": (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M5.7 9.3a2.9 2.9 0 0 0 4.6 0" />
      <circle cx="6.2" cy="6.4" r=".8" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="6.4" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  trash: (
    <>
      <path d="M3.6 5h8.8" />
      <path d="M6.4 5V3.8h3.2V5" />
      <path d="M5 5.4l.5 7a.9.9 0 0 0 .9.8h3.2a.9.9 0 0 0 .9-.8l.5-7" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="2.9" />
      <path d="M8 1.8v1.5M8 12.7v1.5M1.8 8h1.5M12.7 8h1.5M3.7 3.7l1 1M11.3 11.3l1 1M12.3 3.7l-1 1M4.7 11.3l-1 1" />
    </>
  ),
  moon: <path d="M13 9.6A5.6 5.6 0 0 1 6.4 3a5.6 5.6 0 1 0 6.6 6.6Z" />,
  eye: (
    <>
      <path d="M1.8 8S4.3 3.9 8 3.9 14.2 8 14.2 8 11.7 12.1 8 12.1 1.8 8 1.8 8Z" />
      <circle cx="8" cy="8" r="1.9" />
    </>
  ),
  plus: <path d="M8 4v8M4 8h8" />,
  check: <path d="m3.6 8.4 3 3 5.8-6.4" />,
  signout: (
    <>
      <path d="M9.6 11.6v1a1.4 1.4 0 0 1-1.4 1.4H4.2a1.4 1.4 0 0 1-1.4-1.4V3.4A1.4 1.4 0 0 1 4.2 2h4a1.4 1.4 0 0 1 1.4 1.4v1" />
      <path d="M7 8h6.2" />
      <path d="m11.2 6 2 2-2 2" />
    </>
  ),
  spark: <path d="M8 2.2l1.5 4.3L13.8 8l-4.3 1.5L8 13.8l-1.5-4.3L2.2 8l4.3-1.5Z" />,
  diamond: <path d="M8 2.8 13.2 8 8 13.2 2.8 8Z" />,
  alert: (
    <>
      <path d="M8 2.9 14 13.1H2Z" />
      <path d="M8 6.6v3.1" />
      <circle cx="8" cy="11.5" r=".75" fill="currentColor" stroke="none" />
    </>
  ),
  // Beyond the handoff's set, drawn to the same grid and stroke. The reference
  // has no burger because its nav is a permanent rail; this app also has a
  // mobile drawer, and `menu` there is the three-dot overflow mark, which does
  // not read as "open navigation".
  hamburger: <path d="M3 4.8h10M3 8h10M3 11.2h10" />,
  // A hollow counterpart to `check`, for the not-yet-done half of a checklist.
  // Drawn rather than reusing a text bullet so the two marks in a list share
  // one optical weight and baseline.
  circle: <circle cx="8" cy="8" r="4.6" />,
  // The filled twin of `circle`, for a live/on state where `check` would
  // over-claim -- "on" is not "done". Radius matches the dots in `menu` so a
  // status dot and an overflow dot read as the same mark at the same size.
  dot: <circle cx="8" cy="8" r="3.2" fill="currentColor" stroke="none" />,
  // The collapsed twin of `chevron-down`. Drawn rather than rotated with CSS
  // so its stroke caps land on the same grid as every other mark, and so a
  // disclosure control can cross-fade between the two without a transform.
  "chevron-right": <path d="m6.4 4.4 3.6 3.6-3.6 3.6" />,
} as const;

export type IconName = keyof typeof PATHS;

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

/**
 * An icon mark. Sized by the `font-size` of whatever contains it (the `.i`
 * rules set the svg to `1em`), so callers control size with type, not props —
 * that is what keeps an icon optically matched to the label beside it.
 *
 * `title` promotes the mark to an accessible image; without it the icon stays
 * `aria-hidden` and the surrounding button is expected to carry the label.
 */
export function Icon({ name, title }: { name: IconName; title?: string }) {
  return (
    <span className="i" data-i={name} role={title ? "img" : undefined} aria-label={title}>
      <svg
        viewBox="0 0 16 16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        aria-hidden="true"
      >
        {PATHS[name]}
      </svg>
    </span>
  );
}
